import { ChangeEvent, ReactElement, useEffect, useMemo, useState } from "react";

import { gql, TypedDocumentNode, useMutation, useQuery } from "@apollo/client";
import ClearIcon from "@material-ui/icons/Clear";
import DateRangeIcon from "@material-ui/icons/DateRange";
import { DatePicker } from "@material-ui/pickers";
import {
  Box,
  Button,
  ButtonGroup,
  FloatInput,
  FormControl,
  FormLabel,
  Grid,
  Icon,
  IconButton,
  makeStyles,
  QuoteUrgency,
  TextInput,
  Typography,
} from "@portex-pro/ui-components";
import FormStringConcat from "@portex-pro/ui-components/Forms/FormStringConcat";
import YesNoRadio from "components/YesNoRadio";
import capitalize from "lodash/capitalize";
import find from "lodash/find";
import keys from "lodash/keys";
import upperCase from "lodash/upperCase";
import { DateTime } from "luxon";
import { Trans, useTranslation } from "react-i18next";

import {
  FclLoadSpec,
  Mutation,
  MutationUpdateFclLoadSpecArgs,
  PackagingType,
  Query,
  QueryGetQuoteRequestArgs,
  QuoteRequest,
  RoutingType,
  UnloadingDetails,
} from "../../../../../../../api/types/generated-types";
import LayoutColumnTwo from "../../../../../../../components/LayoutColumnTwo";
import Loading from "../../../../../../../components/Loading";
import { useOnApolloError } from "../../../../../../../hooks/useOnApolloError";
import { deserializeCargoDates } from "../../../../../../../utils/deserializeCargoDates";
import { deserializeNotes } from "../../../../../../../utils/deserializeNotes";
import { serializeNotes } from "../../../../../../../utils/serializeNotes";
import { toLuxon } from "../../../../../../../utils/toLuxon";
import { toLuxonRelative } from "../../../../../../../utils/toLuxonRelative";
import FormSelectView from "../../../components/FormSelectView";
import Section from "../../../components/Section";
import { StepperStepProps } from "../../../components/StepperStep";
import StepTitle from "../../../components/StepTitle";
import { packagingTypeList } from "../../../constants/packagingTypes";
import { useGoToStep, useStepStates } from "../../../hooks/useStep";
import { getRoutingTypeLabel } from "../../../utils/getRoutingTypeLabel";
import { getUrgencyOptions } from "../utils/getUrgencyOptions";
import ContainersSummary from "./ContainersSummary";

const ADDRESS = gql`
  fragment ShipmentDetailsStepFCL_Address on Address {
    id
    city
    province_code
    province_name
    iana_timezone
  }
`;

const STOP = gql`
  fragment ShipmentDetailsStepFCL_Stop on Stop {
    id
    address {
      ...ShipmentDetailsStepFCL_Address
    }
    position
  }
  ${ADDRESS}
`;

const FCL_LOAD_SPEC = gql`
  fragment ShipmentDetailsStepFCL_FclLoadSpec on FclLoadSpec {
    id
    incoterms
    routing_type
    cargo_ready_date
    target_delivery_date
    is_overweight
    include_customs_clearance
    commodities
    containers {
      id
      size
      type
      weight
      volume
      min_temp
      max_temp
      requires_genset
    }
    weight_unit
    quote_request {
      id
      stops {
        id
        address {
          id
          iana_timezone
        }
      }
    }
    is_palletized
    pallet_count
    packaging_type
    packing_count
  }
`;

const QUOTE_REQUEST = gql`
  fragment ShipmentDetailsStepFCL_QuoteRequest on QuoteRequest {
    id
    mode
    type
    note
    deadline_respond_at
    stops {
      ...ShipmentDetailsStepFCL_Stop
    }
    fcl_load_spec {
      ...ShipmentDetailsStepFCL_FclLoadSpec
    }
    is_hazardous
    insurance_required
    hazardous_goods_details
    unloading_details
    reference_number
    goods_value
  }
  ${STOP}
  ${FCL_LOAD_SPEC}
`;

const GET_QUOTE_REQUEST: TypedDocumentNode<Pick<Query, "getQuoteRequest">, QueryGetQuoteRequestArgs> = gql`
  query ($id: ID!) {
    getQuoteRequest(id: $id) {
      ...ShipmentDetailsStepFCL_QuoteRequest
    }
  }
  ${QUOTE_REQUEST}
`;

const UPDATE_FCL_LOAD_SPEC: TypedDocumentNode<Pick<Mutation, "updateFclLoadSpec">, MutationUpdateFclLoadSpecArgs> = gql`
  mutation ($input: MutateFclLoadSpecInput!) {
    updateFclLoadSpec(input: $input) {
      id
    }
  }
`;

const UPDATE_QUOTE_REQUEST = gql`
  mutation ($input: UpdateQuoteRequestInput!) {
    updateQuoteRequest(input: $input) {
      id
    }
  }
`;

type ShipmentDetailsStepProps = StepperStepProps;

type QuoteRequestPatch = {
  deadline_respond_at?: DateTime | null;
  note?: QuoteRequest["note"];
  hazardous_goods_details?: QuoteRequest["hazardous_goods_details"];
  reference_number?: QuoteRequest["reference_number"];
  is_hazardous?: QuoteRequest["is_hazardous"];
  insurance_required?: QuoteRequest["insurance_required"];
  type?: QuoteRequest["type"];
  unloading_details?: QuoteRequest["unloading_details"];
  goods_value?: QuoteRequest["goods_value"];
};

const useStyles = makeStyles(() => ({
  urgency: {
    padding: "9px",
    marginTop: "4px",
    "& .MuiBox-root": {
      justifyContent: "space-between",
      width: "100%",
    },
  },
}));

const ShipmentDetailsStep = ({
  active,
  prevStep,
  nextStep,
  goToStep,
  onGoToStep,
  onLoading,
}: ShipmentDetailsStepProps): ReactElement => {
  const { t } = useTranslation(["common", "shipper"]);
  const { onApolloError } = useOnApolloError({ componentName: "ShipmentDetailsStep" });
  const step = useStepStates({ onLoading });
  const { dirty, setDirty, loading, setLoading, quoteRequestId } = step;
  const classes = useStyles();
  const [urgencyOptions] = useState(getUrgencyOptions());

  const [quoteRequestPatch, setQuoteRequestPatch] = useState<QuoteRequestPatch>({});
  const [fclLoadSpecPatch, setFclLoadSpecPatch] = useState<Partial<FclLoadSpec>>({});

  const [updateFclLoadSpec, { loading: updatingFclLoadSpec }] = useMutation(UPDATE_FCL_LOAD_SPEC, {
    onError: onApolloError("updateFclLoadSpec"),
  });
  const [updateQuoteRequest, { loading: updatingQuoteRequest }] = useMutation(UPDATE_QUOTE_REQUEST, {
    onError: onApolloError("updateQuoteRequest"),
  });

  const { data, loading: fetchingQuoteRequest } = useQuery(GET_QUOTE_REQUEST, {
    variables: { id: quoteRequestId },
    fetchPolicy: "cache-and-network",
    onError: onApolloError("getQuoteRequest"),
    skip: !active || (!quoteRequestId && active),
  });

  const quoteRequest = data?.getQuoteRequest;
  const { fcl_load_spec: fclLoadSpec } = data?.getQuoteRequest || {};
  const fclLoadSpecId = fclLoadSpec?.id;

  const patchedQuoteRequest = useMemo<QuoteRequestPatch>(() => {
    const { deadline_respond_at, ...rest } = quoteRequest || {};

    const deadlineRespondAtLuxon = deadline_respond_at ? toLuxon(deadline_respond_at) : null;

    return {
      deadline_respond_at: deadlineRespondAtLuxon,
      ...rest,
      ...quoteRequestPatch,
    };
  }, [quoteRequest, quoteRequestPatch]);

  const patchedFclLoadSpec = useMemo(
    () => ({
      ...(fclLoadSpec || {}),
      ...fclLoadSpecPatch,
    }),
    [fclLoadSpecPatch, fclLoadSpec]
  );

  const {
    incoterms,
    routing_type,
    is_overweight,
    include_customs_clearance,
    containers,
    commodities,
    is_palletized,
    pallet_count,
    packaging_type,
    packing_count,
  } = patchedFclLoadSpec || {};

  useEffect(() => {
    if (!fclLoadSpec) return;

    if (is_palletized) {
      setFclLoadSpecPatch((patch) => ({
        ...patch,
        packaging_type: null,
        packing_count: null,
      }));
    } else {
      setFclLoadSpecPatch((patch) => ({
        ...patch,
        pallet_count: null,
      }));
    }
  }, [fclLoadSpec, is_palletized]);

  const handleBackOrNext = async (): Promise<boolean> => {
    let success = true;

    if (dirty) {
      const note = serializeNotes(quoteRequestPatch.note);
      setLoading(true);
      const result = await Promise.all([
        keys(fclLoadSpecPatch).length
          ? fclLoadSpecId
            ? updateFclLoadSpec({
                variables: {
                  input: {
                    ...fclLoadSpecPatch,
                    id: fclLoadSpecId,
                  },
                },
              })
            : null
          : null,
        keys(quoteRequestPatch).length
          ? updateQuoteRequest({
              variables: {
                input: {
                  ...quoteRequestPatch,
                  id: quoteRequestId,
                  note,
                },
              },
            })
          : null,
      ]);

      success = result.every((r) => !r?.errors);
      setLoading(false);
      if (success) setDirty(false);
    }

    return success;
  };

  useGoToStep({ ...step, active, goToStep, onGoToStep, handleSync: handleBackOrNext });

  const {
    reference_number,
    deadline_respond_at,
    note,
    is_hazardous,
    hazardous_goods_details,
    type,
    insurance_required,
    unloading_details,
    goods_value,
  } = patchedQuoteRequest;

  useEffect(() => {
    if (unloading_details) return;

    setQuoteRequestPatch((patch) => ({
      ...patch,
      unloading_details: UnloadingDetails.Live,
    }));
    setDirty(true);
  }, [setDirty, unloading_details]);

  if (!active) return <></>;

  if (fetchingQuoteRequest || !data) return <Loading spinnerOnly height="auto" />;

  const { cargo_ready_date, pickup_iana_timezone, target_delivery_date, delivery_iana_timezone } =
    deserializeCargoDates(
      {
        cargo_ready_date: patchedFclLoadSpec.cargo_ready_date,
        target_delivery_date: patchedFclLoadSpec.target_delivery_date,
      },
      patchedFclLoadSpec.quote_request?.stops || []
    );

  const backDisabled = loading;
  const nextDisabled =
    loading ||
    is_overweight == null ||
    include_customs_clearance == null ||
    insurance_required == null ||
    is_hazardous == null ||
    (!!is_hazardous && !hazardous_goods_details) ||
    (!!include_customs_clearance && !commodities) ||
    updatingFclLoadSpec ||
    updatingQuoteRequest ||
    (!!target_delivery_date &&
      !!cargo_ready_date &&
      cargo_ready_date.startOf("day").setZone(delivery_iana_timezone, { keepLocalTime: true }) >
        target_delivery_date.startOf("day"));

  const onReferenceNumberChange = ({
    target: { value: reference_number },
  }: ChangeEvent<{ name?: string; value: string }>) => {
    setQuoteRequestPatch((patch) => ({ ...patch, reference_number }));
    setDirty(true);
  };

  const handleChangeCargoReadyDate = (nextDate: DateTime) => {
    setFclLoadSpecPatch((current) => ({
      ...current,
      cargo_ready_date: nextDate.setZone(pickup_iana_timezone ?? "", { keepLocalTime: true }).toJSDate(),
    }));
    setDirty(true);
  };

  const handleChangeTargetDeliveryDate = (nextDate: DateTime) => {
    setFclLoadSpecPatch((current) => ({
      ...current,
      target_delivery_date: nextDate.setZone(delivery_iana_timezone ?? "", { keepLocalTime: true }).toJSDate(),
    }));
    setDirty(true);
  };

  const handleClearTargetDeliveryDate = () => {
    setFclLoadSpecPatch((current) => ({ ...current, target_delivery_date: null }));
    setDirty(true);
  };

  const handleChangeOverweight = (is_overweight: boolean) => {
    setFclLoadSpecPatch((current) => ({ ...current, is_overweight }));
    setDirty(true);
  };

  const handleChangeCustoms = (include_customs_clearance: boolean) => {
    setFclLoadSpecPatch((current) => ({ ...current, include_customs_clearance }));
    setDirty(true);
  };

  const handleChangeHazardous = (value: QuoteRequestPatch["is_hazardous"]) => {
    // also clear details when toggle is reset
    setQuoteRequestPatch((current) => ({ ...current, is_hazardous: value, hazardous_goods_details: "" }));
    setDirty(true);
  };

  const handleChangeInsurance = (value: QuoteRequestPatch["insurance_required"]) => {
    setQuoteRequestPatch((current) => ({ ...current, insurance_required: value }));
    setDirty(true);
  };

  const handleChangeGoodsValue = (value: QuoteRequestPatch["goods_value"]) => {
    setQuoteRequestPatch((current) => ({ ...current, goods_value: value }));
    setDirty(true);
  };

  const handleClearPackagingType = () => {
    setFclLoadSpecPatch((current) => ({ ...current, packaging_type: null }));
    setDirty(true);
  };

  const togglePackagingType = (packagingType: PackagingType) => {
    setFclLoadSpecPatch((patch) => ({ ...patch, packaging_type: packagingType }));
    setDirty(true);
  };

  const onPackingCountChange = ({
    target: { value: packing_count },
  }: ChangeEvent<{ name?: string; value: string }>) => {
    const positivePackingCount = Number(packing_count.replace(/\D/g, ""));
    setFclLoadSpecPatch((patch) => ({
      ...patch,
      packing_count: positivePackingCount,
    }));
    setDirty(true);
  };

  const onPalletCountChange = ({ target: { value: pallet_count } }: ChangeEvent<{ name?: string; value: string }>) => {
    const positivePalletCount = Number(pallet_count.replace(/\D/g, ""));
    setFclLoadSpecPatch((patch) => ({
      ...patch,
      pallet_count: positivePalletCount,
    }));
    setDirty(true);
  };

  const handleChangeCommodities = (commodities: string) => {
    setFclLoadSpecPatch((current) => ({ ...current, commodities }));
    setDirty(true);
  };

  const renderUrgencyLabel = (id: string | undefined) => {
    const option = find(urgencyOptions, { id });
    const fallback = toLuxonRelative(deadline_respond_at);
    return option
      ? t("shipper:urgencyLabel", {
          type: capitalize(type ?? ""),
          label: option.label,
        })
      : t("shipper:urgencyLabel", {
          type: capitalize(type ?? ""),
          label: fallback,
        });
  };

  const handleClearUrgency = () => {
    setQuoteRequestPatch((current) => ({ ...current, deadline_respond_at: null }));
    setDirty(true);
  };

  const handleChangeUrgency = (id: string) => {
    const selected = find(urgencyOptions, { id });
    setQuoteRequestPatch((current) => ({ ...current, deadline_respond_at: selected?.value }));
    setDirty(true);
  };

  const onNoteChange = ({ target: { value: note } }: ChangeEvent<{ name?: string; value: string }>) => {
    setQuoteRequestPatch((patch) => ({ ...patch, note }));
    setDirty(true);
  };

  const onHazardousNotesChange = ({
    target: { value: hazardous_goods_details },
  }: ChangeEvent<{ name?: string; value: string }>) => {
    setQuoteRequestPatch((patch) => ({ ...patch, hazardous_goods_details }));
    setDirty(true);
  };

  const toggleIsPalletized = (is_palletized: boolean) => () => {
    setFclLoadSpecPatch((patch) => ({
      ...patch,
      is_palletized,
    }));
    setDirty(true);
  };

  const onUnloadingDetailsChange = (unloading_details: UnloadingDetails) => () => {
    setQuoteRequestPatch((patch) => ({
      ...patch,
      unloading_details,
    }));
    setDirty(true);
  };

  const selectedUrgencyOption = find(
    urgencyOptions,
    (option) =>
      option.value.toUTC().day === deadline_respond_at?.toUTC().day &&
      option.value.toUTC().hour === deadline_respond_at?.toUTC().hour
  );

  const incotermsField = (
    <TextInput margin="dense" translate="no" disabled={true} label="Incoterms" fullWidth value={incoterms} />
  );

  const routingField = (
    <TextInput
      margin="dense"
      translate="no"
      disabled={true}
      label={t("common:routing")}
      fullWidth
      value={getRoutingTypeLabel(routing_type)}
    />
  );

  const cargoReadyDateField = (
    <DatePicker
      // @ts-expect-error: not sure why this is warning as a missing prop, but it works
      TextFieldComponent={TextInput}
      disableToolbar
      disablePast
      autoOk
      PopoverProps={{
        anchorOrigin: {
          vertical: "bottom",
          horizontal: "left",
        },
        transformOrigin: {
          vertical: "top",
          horizontal: "left",
        },
      }}
      fullWidth
      margin="dense"
      variant="inline"
      color="primary"
      label={t("shipper:cargoReadyDate")}
      value={cargo_ready_date}
      // @ts-expect-error: here about dayjs/luxon: should be safe to ignore & it should go away when we fully uninstall dayjs dependency + utils
      onChange={handleChangeCargoReadyDate}
      // // @ts-expect-error: not sure why this is warning as a missing prop, but it works
      startIcon={<Icon as={DateRangeIcon} palette="grey" />}
    />
  );

  const targetDeliveryDateField = (
    <DatePicker
      // @ts-expect-error: not sure why this is warning as a missing prop, but it works
      TextFieldComponent={TextInput}
      disableToolbar
      disablePast
      autoOk
      PopoverProps={{
        anchorOrigin: {
          vertical: "bottom",
          horizontal: "left",
        },
        transformOrigin: {
          vertical: "top",
          horizontal: "left",
        },
      }}
      fullWidth
      margin="dense"
      variant="inline"
      color="primary"
      label={t("shipper:targetDestinationDeliveryDate")}
      placeholder={t("shipper:selectDateOptional")}
      minDate={cargo_ready_date?.setZone(delivery_iana_timezone, { keepLocalTime: true })}
      value={target_delivery_date}
      // @ts-expect-error: here about dayjs/luxon: should be safe to ignore & it should go away when we fully uninstall dayjs dependency + utils
      onChange={handleChangeTargetDeliveryDate}
      // // @ts-expect-error: not sure why this is warning as a missing prop, but it works
      startIcon={<Icon as={DateRangeIcon} palette="grey" />}
      InputProps={{
        endAdornment: target_delivery_date ? (
          <IconButton
            onClick={(e) => {
              e.stopPropagation();
              handleClearTargetDeliveryDate();
            }}
          >
            <ClearIcon />
          </IconButton>
        ) : null,
      }}
    />
  );

  const packingMethodField = (
    <FormControl margin="dense" fullWidth>
      <FormLabel>{t("shipper:packingMethod")}</FormLabel>
      <ButtonGroup fullWidth color={"primary"}>
        <Button onClick={toggleIsPalletized(true)} className={is_palletized ? "Ptx-selected" : ""}>
          {t("shipper:palletized")}
        </Button>
        <Button onClick={toggleIsPalletized(false)} className={!is_palletized ? "Ptx-selected" : ""}>
          {t("shipper:floorLoaded")}
        </Button>
      </ButtonGroup>
    </FormControl>
  );

  const packagingTypeField = (
    <FormSelectView
      onClear={handleClearPackagingType}
      onChange={togglePackagingType}
      label={t("shipper:packingType")}
      value={packaging_type ?? undefined}
      placeholder={t("shipper:setPackingType")}
      formControlProps={{ margin: "dense", fullWidth: true }}
      items={packagingTypeList}
      getItemCopy={(packagingType) => capitalize(packagingType)}
    />
  );

  const packagingCountField = (
    <TextInput
      translate="no"
      placeholder={t("shipper:setPackingType")}
      label={t("shipper:packingCount")}
      value={packing_count || ""}
      margin="dense"
      fullWidth
      onChange={onPackingCountChange}
    />
  );

  const palletCountField = (
    <TextInput
      translate="no"
      label={t("shipper:palletCount")}
      placeholder={t("shipper:setPalletCount")}
      value={pallet_count || ""}
      margin="dense"
      fullWidth
      onChange={onPalletCountChange}
    />
  );

  const unloadingDetailsField = (
    <FormControl margin="dense" fullWidth>
      <FormLabel>{t("common:unloadingDetails")}</FormLabel>
      <ButtonGroup fullWidth color={"primary"}>
        <Button
          onClick={onUnloadingDetailsChange(UnloadingDetails.Live)}
          className={unloading_details === UnloadingDetails.Live ? "Ptx-selected" : ""}
        >
          {t("common:liveUnload")}
        </Button>
        <Button
          onClick={onUnloadingDetailsChange(UnloadingDetails.Drop)}
          className={unloading_details === UnloadingDetails.Drop ? "Ptx-selected" : ""}
        >
          {t("common:dropAndPick")}
        </Button>
      </ButtonGroup>
    </FormControl>
  );

  const overweightRadio = (
    <FormControl fullWidth margin={"dense"}>
      <FormLabel>{t("shipper:shipmentDetailsStep.overweightLabel")}</FormLabel>
      <YesNoRadio value={is_overweight} onChange={handleChangeOverweight} required />
    </FormControl>
  );
  const customsRadio = (
    <FormControl fullWidth margin={"dense"}>
      <FormLabel>{t("shipper:shipmentDetailsStep.customsLabel")}</FormLabel>
      <YesNoRadio value={include_customs_clearance} onChange={handleChangeCustoms} required />
    </FormControl>
  );
  const hazardousRadio = (
    <FormControl fullWidth margin={"dense"}>
      <FormLabel>{t("shipper:shipmentDetailsStep.hazardousLabel")}</FormLabel>
      <YesNoRadio value={is_hazardous} onChange={handleChangeHazardous} required />
    </FormControl>
  );

  const insuranceRadio = (
    <FormControl fullWidth margin={"dense"}>
      <FormLabel>{t("shipper:shipmentDetailsStep.insuranceLabel")}</FormLabel>
      <YesNoRadio value={insurance_required} onChange={handleChangeInsurance} required />
    </FormControl>
  );

  const goodsValueField = (
    <FloatInput
      label={t("common:goodsValueOptional")}
      startIcon={<Box ml={1}>$</Box>}
      value={goods_value ?? 0}
      placeholder={t("common:goodsValue")}
      decimalPlace={2}
      onChange={handleChangeGoodsValue}
    />
  );

  const containersSummary = (
    <FormControl fullWidth margin={"dense"}>
      <FormLabel>{t("shipper:shipmentDetailsStep.summaryOfContainers")}</FormLabel>
      <ContainersSummary containers={containers ?? []} />
    </FormControl>
  );

  const commoditiesField = (
    <FormStringConcat
      margin="dense"
      label={t("common:commodities")}
      onChange={handleChangeCommodities}
      value={commodities ?? ""}
      highlight={!!include_customs_clearance && !commodities}
      TextInputProps={{ required: !!include_customs_clearance }}
    />
  );

  const hazardousDetailsField = (
    <TextInput
      multiline
      translate="no"
      rows={3}
      label={t("shipper:shipmentDetailsStep.hazardousDetailsLabel")}
      placeholder={t("shipper:shipmentDetailsStep.hazardousDetailsPlaceholder")}
      fullWidth
      value={hazardous_goods_details ?? ""}
      onChange={onHazardousNotesChange}
      required
      highlight={!hazardous_goods_details}
    />
  );

  const noteField = (
    <TextInput
      multiline
      translate="no"
      rows={3}
      label={t("common:additionalNotes")}
      placeholder={t("shipper:additionalNotesPlaceholder")}
      fullWidth
      margin="dense"
      InputProps={{ style: { paddingTop: 0, paddingBottom: 0 } }}
      value={deserializeNotes(note)}
      onChange={onNoteChange}
    />
  );

  return (
    <LayoutColumnTwo.Content
      noHeadBorder
      active={active}
      loading={loading}
      backProps={{
        disabled: backDisabled,
        onClick: async () => {
          const success = await handleBackOrNext();
          onGoToStep(success, prevStep);
        },
      }}
      next={
        <Box>
          <Button
            disabled={nextDisabled}
            variant={"contained"}
            color={"primary"}
            size={"large"}
            onClick={async () => {
              const success = await handleBackOrNext();
              onGoToStep(success, nextStep);
            }}
          >
            {t("common:next")}
          </Button>
        </Box>
      }
    >
      <Box display="flex" flexDirection="column" height="100%" overflow="auto">
        <Box pt={5} pb={5}>
          <StepTitle
            title={t("shipper:shipmentDetails")}
            subtitle={t("shipper:shipmentDetailsStep.shipmentDetailsSubTitle")}
          />
        </Box>

        <Section title={t("shipper:shipmentDetailsStep.referenceNoUrgency")} variant="grey">
          <Grid container spacing={2}>
            <Grid item sm={6}>
              <TextInput
                fullWidth
                label={
                  <Typography>
                    <Trans i18nKey="shipmentDetailsStep.referenceNoOptional" ns="shipper">
                      <strong>PO/Reference No. </strong>(optional)
                    </Trans>
                  </Typography>
                }
                margin="dense"
                value={reference_number}
                placeholder={t("shipper:shipmentDetailsStep.setReferenceNo")}
                onChange={onReferenceNumberChange}
              />
            </Grid>
            <Grid item sm={6}>
              <FormControl fullWidth margin={"dense"}>
                <FormLabel>{t("shipper:urgency")}</FormLabel>
                <QuoteUrgency
                  className={classes.urgency}
                  data={urgencyOptions}
                  getLabel={renderUrgencyLabel}
                  value={selectedUrgencyOption?.id}
                  onChange={handleChangeUrgency}
                  onClear={() => handleClearUrgency()}
                />
              </FormControl>
            </Grid>
          </Grid>
        </Section>

        <Section title={t("shipper:shipmentDetailsStep.routingAndDates")}>
          <Grid container spacing={2}>
            <Grid item sm={3}>
              {incotermsField}
            </Grid>
            <Grid item sm={3}>
              {routingField}
            </Grid>
            <Grid item sm={3}>
              {cargoReadyDateField}
            </Grid>
            <Grid item sm={3}>
              {targetDeliveryDateField}
            </Grid>
            {routing_type !== RoutingType.P2P && routing_type !== RoutingType.D2P ? (
              <Grid item sm={6}>
                {unloadingDetailsField}
              </Grid>
            ) : null}
          </Grid>
        </Section>

        <Section title={upperCase(t("common:containers"))} variant="grey">
          <Grid container spacing={2}>
            <Grid item sm={6}>
              {packingMethodField}
            </Grid>

            {is_palletized ? (
              <Grid item sm={6}>
                {palletCountField}
              </Grid>
            ) : (
              <>
                <Grid item sm={3}>
                  {packagingTypeField}
                </Grid>
                <Grid item sm={3}>
                  <Grid item sm={12}>
                    {packagingCountField}
                  </Grid>
                </Grid>
              </>
            )}
            <Grid item sm={6}>
              {containersSummary}
            </Grid>
            <Grid item sm={6}>
              {overweightRadio}
            </Grid>
          </Grid>
        </Section>

        <Section title={upperCase(t("common:commodities"))}>
          <Grid container spacing={2}>
            <Grid item sm={6}>
              {commoditiesField}
            </Grid>
            <Grid item sm={6}>
              {hazardousRadio}
            </Grid>
            {is_hazardous ? (
              <Grid item sm={12}>
                {hazardousDetailsField}
              </Grid>
            ) : null}
            <Grid item sm={6}>
              {insuranceRadio}
            </Grid>
            <Grid item sm={6}>
              {customsRadio}
            </Grid>
            {insurance_required ? (
              <Grid item sm={6}>
                {goodsValueField}
              </Grid>
            ) : null}
          </Grid>
        </Section>

        <Section title="" variant="grey">
          {noteField}
        </Section>
      </Box>
    </LayoutColumnTwo.Content>
  );
};

export default ShipmentDetailsStep;
